import { Dialog, DialogContent, DialogTitle } from "@material-ui/core";
import { CloseOutlined } from "@material-ui/icons";
import MyTypography from "@saleor/components/MyTypography";
import { useSearchSuggestionMaterialLazyQuery } from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { mapEdgesToItems } from "@saleor/utils/maps";
import uniqBy from "lodash/uniqBy";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import InputSuggestion from "../InputSuggestion";
import SearchPMTModal from "../SearchPMTModal";

interface RouterProps {
  loading?: boolean;
  open?: boolean;
  onClose?: () => void;
  handleAddToOrder?: (event) => void;
  initialValue?: {
    materialCode?: string;
  };
  headerData?: any;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      "& .MuiPaper-root": {
        minWidth: 600,
        boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.25)",
        borderRadius: 16,
        padding: 24,
        overflow: "auto",
        "@media (max-width: 600px)": {
          minWidth: "90%"
        }
      },
      "& .MuiDialogActions-root": {
        borderTop: 0,
        padding: "24px 0 0 0",
        gap: 24
      }
    },
    dialogTitle: {
      padding: 0,
      "& .MuiTypography-root": {
        display: "flex",
        alignItems: "center"
      }
    },
    mainTitle: {
      color: theme.palette.info.main
    },
    closeIcon: {
      marginLeft: "auto",
      cursor: "pointer"
    },
    dialogCenter: {
      padding: "0px !important",
      marginTop: "24px !important",
      "& .MuiFormControl-root": {
        height: 40
      },
      "& .MuiSelect-root b": {
        fontWeight: 400,
        fontSize: "1.6rem"
      }
    },
    buttonAction: {
      width: "30%",
      height: 38,
      "& svg": {
        marginRight: 10
      },
      "@media (max-width: 600px)": {
        width: "50%"
      }
    },
    nameField: {
      marginBottom: 10
    },
    inputField: {
      marginBottom: 20
    },
    bold: {
      fontWeight: 700
    },
    size16: {
      fontSize: "1.6rem"
    },
    boxBackground: {
      borderRadius: 4,
      background: theme.palette.background.default,
      padding: "9px 16px",
      height: 40
    },
    radioGroup: {
      flexDirection: "row"
    },
    checkboxBackground: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "#D4F4F8",
      padding: "0px 10px",
      marginTop: 20,
      fontSize: "1.6rem",
      borderRadius: "4px 4px 0px 0px",
      border: `1px solid ${theme.palette.grey[300]}`,
      borderBottom: 0
    },
    checkboxBefore: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "#ffffff",
      borderRadius: "4px",
      border: `1px solid ${theme.palette.grey[300]}`,
      padding: "0px 10px",
      marginTop: 20,
      fontSize: "1.6rem"
    },
    background: {
      background: "#F2FAFB !important",
      padding: "9px 12px",
      borderRadius: "0px 0px 4px 4px",
      border: `1px solid ${theme.palette.grey[300]}`,
      borderTop: 0
    },
    textError: {
      color: theme.palette.error.light,
      marginTop: 5,
      fontSize: 12
    },
    required: {
      color: theme.palette.error.main,
      marginLeft: 1,
      "& :after": {
        content: "*"
      }
    },
    selectField: {
      height: "36px",
      "& .MuiSelect-root": {
        padding: "0.8rem 1.2rem",
        color: theme.palette.grey[800],
        display: "flex"
      }
    }
  }),
  { name: "ModalSearchForProducts" }
);

const ModalSearchForProducts: React.FC<RouterProps> = props => {
  const {
    loading = false,
    open,
    onClose,
    initialValue,
    handleAddToOrder,
    headerData
  } = props;
  const classes = useStyles();
  const intl = useIntl();
  const [dataValues, setDataValues] = useState(initialValue);

  const [
    getDataSuggestions,
    { data: fetchDataSuggestions, loading: loadingDataSuggestion }
  ] = useSearchSuggestionMaterialLazyQuery({ fetchPolicy: "no-cache" });

  useEffect(() => {
    if (open) {
      if (initialValue) {
        setDataValues(initialValue);
      }
    }
  }, [open]);
  const materialCodePayload = {
    saleOrg: headerData.values.salesOrganizationCode,
    distributionChannel: headerData.values.distributionChannelCode,
    searchBy: "MAT_CODE"
  };
  const customerMaterialPayload = {
    saleOrg: headerData.values.salesOrganizationCode,
    distributionChannel: headerData.values.distributionChannelCode,
    searchBy: "CUST_MAT_CODE",
    soldTo: headerData.values.soldToCode
  };

  const searchSuggestionOptions = useMemo(() => {
    let data = [];
    if (fetchDataSuggestions?.searchSuggestionMaterial) {
      data = mapEdgesToItems(fetchDataSuggestions?.searchSuggestionMaterial);
    }
    const res = data.map(data => ({
      title: data.displayText,
      code: data.materialCode,
      value: data.id
    }));
    return uniqBy(res, "value");
  }, [fetchDataSuggestions?.searchSuggestionMaterial]);

  const handleChangeValue = e => {
    const { name, value } = e.target;
    const data = { ...dataValues, [name]: value };
    setDataValues(data);
    if (data.materialCode) {
      onClose();
      handleAddToOrder(data.materialCode);
    }
  };

  return (
    <Dialog
      className={classes.root}
      open={open}
      data-testid="modalSearchForProducts"
    >
      <DialogTitle className={classes.dialogTitle}>
        <MyTypography variant={"h6"} className={classes.mainTitle}>
          {intl.formatMessage({ defaultMessage: "ค้นหาสินค้า" })}
        </MyTypography>
        <CloseOutlined
          id="closeModal"
          data-testid="close-modal"
          className={classes.closeIcon}
          onClick={onClose}
        />
      </DialogTitle>
      <DialogContent className={classes.dialogCenter}>
        <MyTypography variant={"subtitle3"} className={classes.nameField}>
          {intl.formatMessage({
            defaultMessage: "Material Code / Description"
          })}
        </MyTypography>
        <div className={classes.inputField}>
          <InputSuggestion
            contained
            isWidthAuto
            isSearchIcon
            isCheckingRole
            disableDuplicationLoading
            searchField="matCode"
            placeholder="พิมพ์ค้นหา"
            dataOptions={searchSuggestionOptions}
            fetchDataSuggestions={fetchDataSuggestions}
            loadingDataSuggestion={loadingDataSuggestion}
            getDataSuggestions={getDataSuggestions}
            payload={materialCodePayload}
            totalCount={
              fetchDataSuggestions?.searchSuggestionMaterial?.edges?.length
            }
            handleSelectedSearch={e => {
              const selected = searchSuggestionOptions?.find(
                x => x.title === e
              );
              handleChangeValue({
                target: {
                  name: "materialCode",
                  value: selected?.code || ""
                }
              });
            }}
            id="matCodeSearch"
          />
        </div>
        <MyTypography variant={"subtitle3"} className={classes.nameField}>
          {intl.formatMessage({ defaultMessage: "Customer Material Code" })}
        </MyTypography>
        <div className={classes.inputField}>
          <InputSuggestion
            contained
            isWidthAuto
            isSearchIcon
            isCheckingRole
            disableDuplicationLoading
            searchField="custMatCode"
            dataOptions={searchSuggestionOptions}
            fetchDataSuggestions={fetchDataSuggestions}
            loadingDataSuggestion={loadingDataSuggestion}
            getDataSuggestions={getDataSuggestions}
            payload={customerMaterialPayload}
            totalCount={
              fetchDataSuggestions?.searchSuggestionMaterial?.edges?.length
            }
            placeholder="พิมพ์ค้นหา"
            handleSelectedSearch={e => {
              const selected = searchSuggestionOptions?.find(
                x => x.title === e
              );
              handleChangeValue({
                target: {
                  name: "materialCode",
                  value: selected?.code || ""
                }
              });
            }}
            id="custMatCodeSearch"
          />
        </div>
        <MyTypography variant={"subtitle3"} className={classes.nameField}>
          {intl.formatMessage({ defaultMessage: "Product Catalog" })}
        </MyTypography>
        <SearchPMTModal
          loading={loading}
          handleAddToOrder={handleAddToOrder}
          saleOrg={headerData.values.salesOrganizationCode}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ModalSearchForProducts;
